import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// API/Components/Resources
import axiosInstance from '../axios';
import BatteryMapCell from './battery_map_cell';
import { buildUnitTable } from './table_utils';

// MaterialUI
import Container from '@mui/material/TableContainer';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';


function StringDataLoading(Component) {
	return function DataLoadingComponent({ isLoading, ...props }) {
		if (!isLoading) return <Component {...props} />;
		return (
			<Box
				sx={{
					bgcolor: '#FFFFFF44',
					border: 1,
					borderColor: '#CCCCCC',
					borderRadius: 1,
					height: '100%'
				}}
			>
				<Grid container>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<div height='15px'/>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<div height='15px'/>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<div height='15px'/>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
					<Grid container>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={50} height={50}/>
					</Grid>
				</Grid>
			</Box>
		);
	};
}

function StringMap(props) {
	const {
		locationNum,
		stringNumber,
		numberUnits,
		unitShape,
		numberColumns,
		numberRows,
		units,
		scale,
	} = props;
	const [tableData, setTableData] = useState([]);
	const [stringData, setStringData] = useState({
		locationNumber: locationNum,
		stringNumber: stringNumber,
		numberUnits: numberUnits,
	});

	const cellSize = 35 * scale;

	useEffect(() => {
		const unitTable = buildUnitTable({
			unitShape,
			numberColumns,
			numberRows,
			units
		});
		setTableData(unitTable);
		setStringData({
			locationNumber: locationNum,
			stringNumber: stringNumber,
			numberUnits: numberUnits
		});
	}, [unitShape, numberColumns, numberRows, units, locationNum, stringNumber, numberUnits]);

	return (
		<Container align="center">
			<Box overflow="auto">
				<table>
					<tbody>
						{tableData.map((row, rowIndex) => (
							<tr key={rowIndex}>
								{row.map((cell, columnIndex) => (
									<BatteryMapCell
										key={`${rowIndex},${columnIndex}`}
										cell={cell}
										rowIndex={rowIndex}
										columnIndex={columnIndex}
										cellSize={cellSize}
										locationNum={locationNum}
										stringNum={stringData.stringNumber}
										scale={scale}
									/>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Box>
		</Container>
	);
}

export default function LoadStringMap(props) {
	
	const { locationNum, stringNum, scale = 1.0 } = props;

	const StringMapLoading = StringDataLoading(StringMap);
	const [stringState, setStringState] = useState({
		isLoading: true,
		locationNum: locationNum,
		stringNumber: stringNum,
		numberUnits: 0,
		unitShape: 0,
		numberColumns: 0,
		numberRows: 0,
		units: [],
		scale: scale,
	});
	
	const navigate = useNavigate();

	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/status/string_${stringNum}`)
			.then(res => {
				const stringData = res.data;
				setStringState({
					isLoading: false,
					locationNum: locationNum,
					stringNumber: stringData.string_number,
					numberUnits: stringData.number_units,
					unitShape: stringData.unit_shape,
					numberColumns: stringData.number_columns,
					numberRows: stringData.number_rows,
					units: stringData.units,
					scale: scale
				})
			})
			.catch(err => {
				console.error("Error fetching string status data: ", err);
				navigate('/404');
			});
	}, [stringNum, locationNum, scale, navigate]);

	return (
		<StringMapLoading
			isLoading={stringState.isLoading}
			locationNum={stringState.locationNum}
			stringNumber={stringState.stringNumber}
			numberUnits={stringState.numberUnits}
			unitShape={stringState.unitShape}
			numberColumns={stringState.numberColumns}
			numberRows={stringState.numberRows}
			units={stringState.units}
			scale={stringState.scale}
		/>
	);
}